import { useFormikContext } from 'formik';
import { Col, Row } from 'react-bootstrap';

import { FormPanel } from '@components/FormPanel/FormPanel';
import { BIOLOGICAL_SEX_OPTIONS, EXTENDED_RELATIONSHIP_OPTIONS } from '@customTypes/constants';
import InputField from '@pages/order/components/common/InputField';
import SelectField from '@pages/order/components/common/SelectField';
import { isMinorDOB } from '@utils/dates';

import { FVTFamilyMemberFormValues } from '../FvtInvite.validation';

export const InviteFormFields = () => {
  const formik = useFormikContext<FVTFamilyMemberFormValues>();
  const showGuardianFields = isMinorDOB(formik.values.dob);

  return (
    <FormPanel.Body className='form-panel__body--flush'>
      <div className='px-4 py-3'>
        <Row className='g-3'>
          <Col md={6}>
            <InputField
              id='invite-first_name'
              error={formik.errors.first_name}
              label='First legal name'
              phiSanitize
              required
              showError={!!formik.touched.first_name}
              value={formik.values.first_name}
              onBlur={() => formik.setFieldTouched('first_name', true)}
              onChange={nextValue => formik.setFieldValue('first_name', nextValue)}
            />
          </Col>

          <Col md={6}>
            <InputField
              id='invite-last_name'
              label='Last legal name'
              required
              value={formik.values.last_name}
              showError={!!formik.touched.last_name}
              error={formik.errors.last_name}
              phiSanitize
              onChange={nextValue => formik.setFieldValue('last_name', nextValue)}
              onBlur={() => formik.setFieldTouched('last_name', true)}
            />
          </Col>

          <Col md={4}>
            <SelectField
              id='invite-relationship'
              label='Relationship'
              error={formik.errors.relationship}
              options={EXTENDED_RELATIONSHIP_OPTIONS}
              phiSanitize
              required
              showError={!!formik.touched.relationship}
              value={formik.values.relationship}
              onBlur={() => formik.setFieldTouched('relationship', true)}
              onChange={nextValue => formik.setFieldValue('relationship', nextValue)}
            />
          </Col>

          <Col md={4}>
            <SelectField
              id='invite-sex'
              label='Sex'
              error={formik.errors.sex}
              options={BIOLOGICAL_SEX_OPTIONS}
              phiSanitize
              required
              showError={!!formik.touched.sex}
              value={formik.values.sex}
              onBlur={() => formik.setFieldTouched('sex', true)}
              onChange={nextValue => formik.setFieldValue('sex', nextValue)}
            />
          </Col>

          <Col md={4}>
            <InputField
              id='invite-dob'
              label='Date of birth'
              type='date'
              error={formik.errors.dob}
              phiSanitize
              required
              showError={!!formik.touched.dob}
              value={formik.values.dob}
              onBlur={() => formik.setFieldTouched('dob', true)}
              onChange={nextValue => formik.setFieldValue('dob', nextValue)}
            />
          </Col>

          <Col xs={12}>
            <InputField
              id='invite-email'
              label={showGuardianFields ? 'Email address (optional)' : 'Email address'}
              type='email'
              error={formik.errors.email}
              phiSanitize
              required={!showGuardianFields}
              showError={!!formik.touched.email}
              value={formik.values.email}
              onBlur={() => formik.setFieldTouched('email', true)}
              onChange={nextValue => formik.setFieldValue('email', nextValue)}
            />
          </Col>

          {showGuardianFields && (
            <>
              <Col xs={12}>
                <hr className='mt-3 mb-4' />
                <h3 className='fvt-invite-form__guardian-title'>Guardian&apos;s details</h3>
                <p className='text-muted mb-0'>
                  This relative is a minor, so the invitation is sent to their parent or legal guardian.
                </p>
              </Col>

              <Col md={6}>
                <InputField
                  id='invite-guardian_first_name'
                  label="Guardian's first name"
                  error={formik.errors.guardian_first_name}
                  phiSanitize
                  required
                  showError={!!formik.touched.guardian_first_name}
                  value={formik.values.guardian_first_name}
                  onBlur={() => formik.setFieldTouched('guardian_first_name', true)}
                  onChange={nextValue => formik.setFieldValue('guardian_first_name', nextValue)}
                />
              </Col>

              <Col md={6}>
                <InputField
                  id='invite-guardian_last_name'
                  label="Guardian's last name"
                  error={formik.errors.guardian_last_name}
                  phiSanitize
                  required
                  showError={!!formik.touched.guardian_last_name}
                  value={formik.values.guardian_last_name}
                  onBlur={() => formik.setFieldTouched('guardian_last_name', true)}
                  onChange={nextValue => formik.setFieldValue('guardian_last_name', nextValue)}
                />
              </Col>

              <Col md={6}>
                <InputField
                  id='invite-guardian_relationship'
                  label={`Relationship to ${formik.values.first_name.trim() || 'relative'}`}
                  error={formik.errors.guardian_relationship}
                  placeholder='Parent, Legal guardian, etc.'
                  phiSanitize
                  required
                  showError={!!formik.touched.guardian_relationship}
                  value={formik.values.guardian_relationship}
                  onBlur={() => formik.setFieldTouched('guardian_relationship', true)}
                  onChange={nextValue => formik.setFieldValue('guardian_relationship', nextValue)}
                />
              </Col>

              <Col md={6}>
                <InputField
                  id='invite-guardian_email'
                  label="Guardian's email address"
                  type='email'
                  error={formik.errors.guardian_email}
                  phiSanitize
                  required
                  showError={!!formik.touched.guardian_email}
                  value={formik.values.guardian_email}
                  onBlur={() => formik.setFieldTouched('guardian_email', true)}
                  onChange={nextValue => formik.setFieldValue('guardian_email', nextValue)}
                />
              </Col>
            </>
          )}
        </Row>
      </div>
    </FormPanel.Body>
  );
};
